import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { departmentName, slugify } from "@neus/shared";
import { ApiError } from "../api/client";
import { useEffectivePrice, useProduct, useStock } from "../api/hooks";
import { track } from "../api/track";
import { ContractorTag, Price, QtyStepper, StockBadge } from "../components/product/bits";
import { MAX_QTY, useCart } from "../store/cart";
import { ContentPage } from "../components/content/ContentPage";
import { NOT_FOUND_PAGE } from "../content";
import styles from "./pages.module.css";
import { features } from "../features";
import { BRAND } from "@neus/shared";

export function ProductDetailPage() {
  const { sku } = useParams();
  const navigate = useNavigate();
  const { data: product, isLoading, error } = useProduct(sku);
  const stock = useStock(product);
  const pricing = useEffectivePrice(product);
  const add = useCart((s) => s.add);
  const inCart = useCart((s) => s.items.find((i) => i.sku === sku)?.qty ?? 0);
  const [imageIndex, setImageIndex] = useState(0);
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);

  const available = Math.min(MAX_QTY, Math.max(0, stock - inCart));
  // Keep the chosen quantity within what's available as stock and the cart change. Adjusting
  // state during render (React re-renders before committing) replaces the old effect, so the
  // committed qty is always in range and doesn't jump back up when stock returns.
  const clampedQty = Number.isFinite(available) ? Math.max(1, Math.min(qty, available)) : 1;
  if (qty !== clampedQty) setQty(clampedQty);
  useEffect(() => {
    if (!product) return;
    document.title = `${product.name} | ${BRAND.name}`;
    return () => {
      document.title = BRAND.site.title;
    };
  }, [product]);
  // One view per product page visit (not on stock or other data refreshes).
  const viewedSku = product?.sku;
  useEffect(() => {
    if (viewedSku) track({ type: "view", sku: viewedSku });
  }, [viewedSku]);

  if (error instanceof ApiError && error.status === 404) return <ContentPage page={NOT_FOUND_PAGE} />;
  if (isLoading || !product) {
    return (
      <div className="container section">
        {error ? <div className="notice notice--warn">We couldn&rsquo;t load this product.</div> : <p>Loading…</p>}
      </div>
    );
  }

  const image = product.images[imageIndex] ?? product.images[0];
  const attributes = Object.entries(product.attributes);

  return (
    <>
      <div className="page-header">
        <div className="container breadcrumbs">
          <Link to="/">Home</Link> / <Link to={`/${product.department}`}>{departmentName(product.department)}</Link> /{" "}
          {product.name}
        </div>
      </div>

      <div className={`container section ${styles.detail}`}>
        <div className={styles.gallery}>
          <div className={styles.galleryMain}>{image && <img src={image.url} alt={image.alt || product.name} />}</div>
          {product.images.length > 1 && (
            <div className={styles.thumbs}>
              {product.images.map((img, i) => (
                <button
                  key={img.url}
                  type="button"
                  className={i === imageIndex ? styles.thumbActive : undefined}
                  onClick={() => setImageIndex(i)}
                  aria-label={`Show image ${i + 1}`}
                >
                  <img src={img.url} alt="" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className={styles.info}>
          <Link to={`/brands/${slugify(product.brand)}`} className={styles.detailBrand}>
            {product.brand}
          </Link>
          <h1>{product.name}</h1>
          <p className="muted">SKU: {product.sku}</p>

          {pricing?.regularPrice !== undefined && <ContractorTag />}
          <div className={styles.priceRow}>
            <Price
              price={pricing?.price ?? product.price}
              compareAt={product.compareAtPrice}
              regularPrice={pricing?.regularPrice}
            />
            <StockBadge stock={stock} />
          </div>

          <div className={styles.buyBox}>
            {available > 0 ? (
              <>
                <QtyStepper value={qty} max={available} onChange={setQty} />
                <button
                  type="button"
                  className="btn btn--lg"
                  onClick={() => {
                    add(product, qty);
                    setAdded(true);
                  }}
                >
                  Add to cart
                </button>
              </>
            ) : (
              <button type="button" className="btn btn--lg" disabled>
                {stock <= 0 ? "Out of stock" : "All available stock is in your cart"}
              </button>
            )}
          </div>
          {added && (
            <div className="notice" role="status">
              Added to your cart.{" "}
              <button type="button" className="btn btn--ghost" onClick={() => navigate("/cart")}>
                View cart
              </button>
            </div>
          )}
          {inCart > 0 && !added && <p className="muted">{inCart} already in your cart.</p>}

          {features.storeLocations && product.location && (
            <>
              <h2 className={styles.subhead}>In the store</h2>
              <p>
                Find it in <strong>{product.location}</strong> at {BRAND.name}.
              </p>
            </>
          )}

          <h2 className={styles.subhead}>Description</h2>
          <p>{product.description}</p>

          {attributes.length > 0 && (
            <>
              <h2 className={styles.subhead}>Specifications</h2>
              <table className={styles.specs}>
                <tbody>
                  <tr>
                    <th scope="row">SKU</th>
                    <td>{product.sku}</td>
                  </tr>
                  <tr>
                    <th scope="row">Brand</th>
                    <td>{product.brand}</td>
                  </tr>
                  {attributes.map(([k, v]) => (
                    <tr key={k}>
                      <th scope="row">{k}</th>
                      <td>{v}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      </div>
    </>
  );
}
